"use client";

import { type QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { httpBatchStreamLink, loggerLink } from "@trpc/client";
import { createTRPCReact } from "@trpc/react-query";
import type { inferRouterInputs, inferRouterOutputs } from "@trpc/server";
import { useState } from "react";
import SuperJSON from "superjson";

import type { AppRouter } from "@/server/api/root";
import { createQueryClient } from "./query-client";
import { createBrowserSupabaseClient } from "@/lib/supabase-client";

let clientQueryClientSingleton: QueryClient | undefined = undefined;
const getQueryClient = () => {
	if (typeof window === "undefined") {
		// Server: always make a new query client
		return createQueryClient();
	}
	// Browser: use singleton pattern to keep the same query client
	clientQueryClientSingleton ??= createQueryClient();

	return clientQueryClientSingleton;
};

export const api = createTRPCReact<AppRouter>();
export const trpc = api; // Alias for backward compatibility

/**
 * Inference helper for inputs.
 *
 * @example type HelloInput = RouterInputs['example']['hello']
 */
export type RouterInputs = inferRouterInputs<AppRouter>;

/**
 * Inference helper for outputs.
 *
 * @example type HelloOutput = RouterOutputs['example']['hello']
 */
export type RouterOutputs = inferRouterOutputs<AppRouter>;

export function TRPCReactProvider(props: { children: React.ReactNode }) {
	const queryClient = getQueryClient();

	const [trpcClient] = useState(() =>
		api.createClient({
			links: [
				// Only in development so production bundle can tree-shake logger (reduces unused JS)
				...(process.env.NODE_ENV === "development"
					? [
							loggerLink({
								enabled: (op) =>
									(op.direction === "down" && op.result instanceof Error) || true,
							}),
						]
					: []),
				httpBatchStreamLink({
					transformer: SuperJSON,
					url: `${getBaseUrl()}/api/trpc`,
					headers: async () => {
						const headers = new Headers();
						headers.set("x-trpc-source", "nextjs-react");
						
						// Using Supabase PKCE session from browser
						if (typeof window !== "undefined") {
							const supabase = createBrowserSupabaseClient();
							const { data } = await supabase.auth.getSession();
							if (data.session?.access_token) {
								headers.set("authorization", `Bearer ${data.session.access_token}`);
							}
						}
						
						return headers;
					},
					fetch(url, options) {
						return fetch(url, {
							...options,
							credentials: 'include', // This ensures cookies are sent with requests
						});
					},
				}),
			],
		}),
	);

	return (
		<QueryClientProvider client={queryClient}>
			<api.Provider client={trpcClient} queryClient={queryClient}>
				{props.children}
			</api.Provider>
		</QueryClientProvider>
	);
}

// Alias for backward compatibility
export const TRPCProvider = TRPCReactProvider;

function getBaseUrl() {
	if (typeof window !== "undefined") return window.location.origin;
	if (process.env.VERCEL_URL) return `https://${process.env.VERCEL_URL}`;
	return `http://localhost:${process.env.PORT ?? 3000}`;
}